<script setup lang="ts">
import { Play } from 'lucide-vue-next'
import { onMounted, onUnmounted, ref } from 'vue'

interface Props {
  src?: string
  alt?: string
  as?: 'image' | 'video'
  mediaClass?: string
  muted?: boolean
  autoplay?: boolean
  loop?: boolean
  controls?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  as: 'image',
  mediaClass: 'h-full w-full object-cover rounded-md',
  muted: true,
  autoplay: false,
  loop: false,
  controls: false,
})

const containerRef = ref<HTMLElement>()
const shouldLoad = ref(false)
let observer: IntersectionObserver | null = null

onMounted(() => {
  if (typeof IntersectionObserver === 'undefined' || !containerRef.value) {
    shouldLoad.value = true
    return
  }

  observer = new IntersectionObserver(
    entries => {
      const targetEntry = entries.find(entry => entry.isIntersecting)
      if (!targetEntry) {
        return
      }

      shouldLoad.value = true
      observer?.disconnect()
      observer = null
    },
    {
      rootMargin: '120px 0px',
    },
  )

  observer.observe(containerRef.value)
})

onUnmounted(() => {
  observer?.disconnect()
  observer = null
})
</script>

<template>
  <div ref="containerRef" class="h-full w-full">
    <img
      v-if="shouldLoad && props.as === 'image' && props.src"
      :src="props.src"
      :alt="props.alt"
      :class="props.mediaClass"
    />
    <div class="w-full h-full relative" v-else-if="shouldLoad && props.as === 'video' && props.src">
      <play class="absolute inset-0 m-auto fill-white size-10 opacity-60" />
      <video
        :src="props.src"
        :class="props.mediaClass"
        :muted="props.muted"
        :autoplay="props.autoplay"
        :loop="props.loop"
        :controls="props.controls"
        playsinline
        preload="metadata"
      />
    </div>
    <div v-else class="flex h-full w-full items-center justify-center bg-gray-800">
      <slot name="placeholder">
        <div class="size-5 animate-spin rounded-full border-2 border-white/15 border-t-white/60"></div>
      </slot>
    </div>
  </div>
</template>
